// "Coaching Signal" banner — a gold panel that slides in from the LEFT edge of the results row after a
// call is scored, but ONLY when one or more skills came in below their expected bar. When every skill
// meets or beats target there is no gap to coach, so the banner stays hidden.
//
// It hangs off the verdict band (.coach-signal-host), so it is naturally scoped to Quick Snapshot: on
// another lens the diagnostic view is hidden and the banner goes with it, returning in whatever state
// it was left in.
//
// States (a single translateX drives the horizontal slide):
//   • hidden    — off to the left, not shown (no gaps, dismissed, or before the first score)
//   • collapsed — a small gold handle tab in the page gutter. THIS is the resting state after a score.
//   • open      — the wide message card, shown ONLY after the user clicks the tab.
// It does not open itself. It used to slide the card in on a timer and auto-collapse after a read
// window; now nothing covers the score unless the user asks for it.

export interface CoachingSignalInfo {
  gapCount: number; // how many scored skills landed below their expected bar
  topSkill: string; // display name of the biggest gap (largest expected-minus-actual)
}

interface InitOpts {
  openGuide: () => void; // open the Getting Started guide at the "prove your impact" section
}

let root: HTMLElement | null = null;
let openGuideFn: () => void = () => {};
let active = false; // a scored call with gaps → the banner should be present
let pending = false; // active but not yet slid in — waiting for the view to be on-screen
let slidIn = false; // the show delay has elapsed and the panel is slid in (is-open)
let collapsed = false;

// Visibility watch: a queued banner waits for the Quick Snapshot view to actually be on-screen.
// Bounded so a user who never opens that view doesn't leave a timer running for the session.
const VISIBILITY_POLL_MS = 200;
const VISIBILITY_GIVE_UP_MS = 30_000;
let visibilityTimer: ReturnType<typeof setInterval> | undefined;
let watchStartedAt = 0;

function startVisibilityWatch(): void {
  if (visibilityTimer !== undefined) return;
  watchStartedAt = Date.now();
  visibilityTimer = setInterval(() => {
    if (!pending || !active) return stopVisibilityWatch();
    if (Date.now() - watchStartedAt > VISIBILITY_GIVE_UP_MS) return stopVisibilityWatch();
    ensureMounted();
    if (root && root.offsetParent !== null) {
      stopVisibilityWatch();
      maybeSlideIn();
    }
  }, VISIBILITY_POLL_MS);
}

function stopVisibilityWatch(): void {
  if (visibilityTimer !== undefined) {
    clearInterval(visibilityTimer);
    visibilityTimer = undefined;
  }
}

/** Mount the banner once inside the Quick Snapshot results row and wire its controls. Safe to call
 *  before the score exists — it stays hidden until showCoachingSignal() runs. */
export function initCoachingSignal(opts: InitOpts): void {
  openGuideFn = opts.openGuide;
  ensureMounted();
}

/** Mount the banner, or RE-mount it if something re-rendered .col-score and detached our node.
 *  The old `if (root) return` guard meant a single detach killed the banner for the rest of the
 *  session: root stayed truthy, so init refused to remount, and every later show() painted a node
 *  that was no longer in the document. Re-appending the SAME element keeps its wired controls. */
function ensureMounted(): void {
  if (root?.isConnected) return;
  // .coach-signal-host is the verdict band on the redesigned Quick Snapshot. It replaced .col-score,
  // the old Final Score column the banner used to hang off; the fallback keeps any surface that still
  // renders that column working. The host must be position:relative — the banner is absolutely
  // positioned against it at top:100%, which is what puts it just below the score.
  const host = document.querySelector('.coach-signal-host') ?? document.querySelector('.col-score');
  if (!host) return;
  if (!root) root = build();
  host.appendChild(root);
}

function build(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'coach-signal';
  el.id = 'coach-signal';
  el.setAttribute('aria-hidden', 'true');

  // The message card (shown when open). A clean rounded card with two small icon controls in its
  // top-right corner — no protruding tab, so it reads as a single element.
  const card = document.createElement('div');
  card.className = 'cs-card';

  const controls = document.createElement('div');
  controls.className = 'cs-controls';
  const min = document.createElement('button');
  min.type = 'button';
  min.className = 'cs-ctl';
  min.dataset.cs = 'toggle';
  min.setAttribute('aria-label', 'Minimize the coaching tip');
  min.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 6l-6 6 6 6"/></svg>';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'cs-ctl';
  close.dataset.cs = 'close';
  close.setAttribute('aria-label', 'Dismiss the coaching tip');
  close.textContent = '×';
  controls.append(min, close);

  const title = document.createElement('h3');
  title.className = 'cs-title';
  title.textContent = 'Coaching tip';

  const msg = document.createElement('p');
  msg.className = 'cs-msg';
  msg.id = 'cs-msg';

  const cta = document.createElement('button');
  cta.type = 'button';
  cta.className = 'cs-cta';
  cta.dataset.cs = 'guide';
  cta.textContent = 'How to prove enablement works ›';

  card.append(controls, title, msg, cta);

  // The tucked handle (shown when collapsed): a bold gold tab with a white arrow that emerges from
  // behind the side menu (see CSS). Only ever visible when the card is hidden, so the two never look glued.
  const nub = document.createElement('button');
  nub.type = 'button';
  nub.className = 'cs-nub';
  nub.dataset.cs = 'toggle';
  nub.setAttribute('aria-label', 'Show the coaching tip');
  nub.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 9.5h9V4l7 8-7 8v-5.5H4z"/></svg>';

  el.append(card, nub);

  el.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('[data-cs]') as HTMLElement | null;
    if (!t) return;
    const action = t.dataset.cs ?? '';
    if (action === 'close') {
      hideCoachingSignal();
    } else if (action === 'guide') {
      openGuideFn();
    } else if (action === 'toggle') {
      collapsed = !collapsed;
      apply();
    }
  });

  return el;
}

/** Score complete with one or more gaps: fill the message and put the banner's TAB on screen. The card
 *  stays shut until the user clicks it. Call hideCoachingSignal() when there are no gaps. */
export function showCoachingSignal(info: CoachingSignalInfo): void {
  ensureMounted();
  if (!root) return;
  const msg = root.querySelector('#cs-msg');
  if (msg) {
    const n = info.gapCount;
    const skills = n === 1 ? 'skill' : 'skills';
    const verb = n === 1 ? 'is' : 'are';
    msg.textContent =
      `${n} ${skills} ${verb} below target. Identify whether you are experiencing an individual gap, ` +
      `global team gap or enablement gap.`;
  }
  active = true;
  pending = true;
  slidIn = false;
  // Start COLLAPSED: the banner shows its tab and waits. It no longer opens itself.
  collapsed = true;
  apply();
  maybeSlideIn();
}

/** Start the slide-in, but ONLY when the panel is on-screen. Importing from Home fires the score while
 *  the Quick Snapshot view is still display:none, so a fixed timer would animate the slide off-screen and
 *  the user would never see it. We defer until the view is shown (onCoachingSignalView → 'diagnostic').
 *  offsetParent is null whenever the element or an ancestor (the inactive view) is display:none. */
function maybeSlideIn(): void {
  if (!pending || !active) return;
  ensureMounted();
  // Not on screen yet. Do NOT just give up and wait to be called again — that made the banner depend
  // on a single view-change event arriving after this point, and twice it did not: mounted after the
  // restore (so the show was dropped), then subscribed after the nav had already fired (so the release
  // never came). Poll until the view is actually visible, so no ordering can strand it again.
  if (!root || root.offsetParent === null) {
    startVisibilityWatch();
    return;
  }
  stopVisibilityWatch();
  pending = false;
  // Reveal the TAB only, and stop there. The card used to slide open on a timer and then auto-collapse
  // after a read window; it now opens solely when the user clicks the tab, so nothing covers the score
  // unless they asked for it.
  slidIn = true;
  collapsed = true;
  apply();
}

/** Wire to onViewChange: when Quick Snapshot becomes visible, a banner that was queued while the view was
 *  hidden (import-from-Home flow) can now slide in on-screen. */
export function onCoachingSignalView(view: string): void {
  if (view === 'diagnostic') maybeSlideIn();
}

/** No gaps, a new run/import/clear, or the user dismissed it: slide the banner fully away. */
export function hideCoachingSignal(): void {
  stopVisibilityWatch(); // a queued watch must not resurrect a banner the user dismissed
  active = false;
  pending = false;
  slidIn = false;
  collapsed = false;
  apply();
}

function apply(): void {
  if (!root) return;
  root.classList.toggle('is-open', slidIn);
  root.classList.toggle('is-collapsed', slidIn && collapsed);
  root.setAttribute('aria-hidden', String(!slidIn));
}
